/**
 * JsTexturePreviewDialog: shows a live preview of a JS texture script's
 * canvas output inside a modal dialog, with debug information and controls.
 *
 * Spawns a standalone textureWorker and renders the received ImageBitmap
 * frames onto a visible <canvas>. Shows script errors inline, along with
 * metadata like dimensions, frame count, and approximate FPS.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wand2, Play, Square, RotateCcw, AlertCircle } from 'lucide-react';
import { parseScriptMetadata } from '@/utils/textureUtils';
import type { ProjectFile } from '@/components/files/FileManager';

interface JsTexturePreviewDialogProps {
  file: ProjectFile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WorkerStatus = 'idle' | 'starting' | 'running' | 'error' | 'stopped';

function JsTexturePreviewCanvas({
  file,
  onStatusChange,
  onError,
  onStatsUpdate,
  stopped,
}: {
  file: ProjectFile;
  onStatusChange: (status: WorkerStatus) => void;
  onError: (error: string | null) => void;
  onStatsUpdate: (stats: { frameCount: number; fps: number }) => void;
  stopped: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workerRef = useRef<Worker | null>(null);
  const frameCountRef = useRef(0);
  const fpsFramesRef = useRef<number[]>([]);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const stoppedRef = useRef(stopped);
  stoppedRef.current = stopped;

  const cleanup = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'STOP' });
      workerRef.current.terminate();
      workerRef.current = null;
    }
    frameCountRef.current = 0;
    fpsFramesRef.current = [];
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof file.content !== 'string') return;

    cleanup();

    const scriptCode = file.content;
    const { width, height, fps } = parseScriptMetadata(scriptCode);

    // Set the canvas resolution to match the script's declared dimensions
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctxRef.current = ctx;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    onStatusChange('starting');
    onError(null);
    frameCountRef.current = 0;
    fpsFramesRef.current = [];
    onStatsUpdate({ frameCount: 0, fps: 0 });

    // Spawn the sandboxed texture worker
    const worker = new Worker(
      new URL('../../worker/textureWorker.ts', import.meta.url),
      { type: 'module' },
    );
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent) => {
      const { type } = event.data as { type: string };

      if (type === 'FRAME') {
        if (stoppedRef.current) return;
        const bitmap = event.data.bitmap as ImageBitmap;
        // Draw the bitmap onto the visible canvas
        ctxRef.current?.drawImage(bitmap, 0, 0);
        bitmap.close();

        // Update stats
        frameCountRef.current++;
        const now = performance.now();
        fpsFramesRef.current.push(now);
        // Keep only timestamps from the last 1 second
        const oneSecAgo = now - 1000;
        fpsFramesRef.current = fpsFramesRef.current.filter(
          (t) => t > oneSecAgo,
        );
        const fps = fpsFramesRef.current.length;

        // Throttle stats updates to avoid excessive re-renders
        if (frameCountRef.current % 10 === 0 || frameCountRef.current <= 3) {
          onStatsUpdate({ frameCount: frameCountRef.current, fps });
        }
        return;
      }

      if (type === 'READY') {
        onStatusChange('running');
        return;
      }

      if (type === 'ERROR') {
        const msg = event.data.message as string;
        onStatusChange('error');
        onError(msg);
        return;
      }
    };

    worker.onerror = (err) => {
      onStatusChange('error');
      onError(err.message || 'Worker failed to load');
    };

    // Start the script (pass fps so the worker applies throttle/static logic)
    worker.postMessage({ type: 'START', scriptCode, width, height, fps });

    return () => {
      cleanup();
    };
  }, [file, cleanup, onStatusChange, onError, onStatsUpdate]);

  // Handle stop/resume by stopping the worker
  useEffect(() => {
    if (stopped && workerRef.current) {
      workerRef.current.postMessage({ type: 'STOP' });
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, [stopped]);

  const scriptCode = typeof file.content === 'string' ? file.content : '';
  const { width, height } = parseScriptMetadata(scriptCode);

  return (
    <div className="flex w-full items-center justify-center">
      <canvas
        ref={canvasRef}
        className="rounded-md border border-border"
        style={{
          maxWidth: '100%',
          maxHeight: '320px',
          width: `${width}px`,
          height: `${height}px`,
          objectFit: 'contain',
          imageRendering: 'auto',
          background: '#1a1a2e',
        }}
      />
    </div>
  );
}

export function JsTexturePreviewDialog({
  file,
  open,
  onOpenChange,
}: JsTexturePreviewDialogProps) {
  const [status, setStatus] = useState<WorkerStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ frameCount: 0, fps: 0 });
  const [stopped, setStopped] = useState(false);
  // Increment to force remount + restart of the canvas
  const [restartKey, setRestartKey] = useState(0);

  // Reset state when dialog opens/closes or file changes
  useEffect(() => {
    if (open) {
      setStatus('idle');
      setError(null);
      setStats({ frameCount: 0, fps: 0 });
      setStopped(false);
    }
  }, [open, file]);

  if (!file) return null;

  const scriptCode = typeof file.content === 'string' ? file.content : '';
  const { width, height, fps } = parseScriptMetadata(scriptCode);

  const handleStop = () => {
    setStopped(true);
    setStatus('stopped');
  };

  const handleRestart = () => {
    setStopped(false);
    setStatus('idle');
    setError(null);
    setStats({ frameCount: 0, fps: 0 });
    setRestartKey((k) => k + 1);
  };

  const statusBadge = () => {
    switch (status) {
      case 'starting':
        return (
          <Badge
            variant="outline"
            className="border-yellow-500/50 text-yellow-500"
          >
            Starting...
          </Badge>
        );
      case 'running':
        return (
          <Badge
            variant="outline"
            className="border-green-500/50 text-green-500"
          >
            Running
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="border-red-500/50 text-red-500">
            Error
          </Badge>
        );
      case 'stopped':
        return (
          <Badge
            variant="outline"
            className="border-muted-foreground/50 text-muted-foreground"
          >
            Stopped
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-xl flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-amber-400" />
            <span className="break-all">{file.filename}</span>
            {statusBadge()}
          </DialogTitle>
        </DialogHeader>

        {/* Metadata bar */}
        <div className="flex flex-wrap items-center gap-4 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <span>JS texture script</span>
          <span>
            {width} × {height}
          </span>
          <span>
            {fps === undefined
              ? 'uncapped fps'
              : fps === 0
                ? 'static (fps=0)'
                : `${fps} fps`}
          </span>
          {status === 'running' && fps !== 0 && (
            <>
              <span>Frames: {stats.frameCount}</span>
              <span>~{stats.fps} FPS</span>
            </>
          )}
          {status === 'running' && fps === 0 && (
            <span>Frames: {stats.frameCount}</span>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="mb-1 font-medium">Script error</div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px]">
                {error}
              </pre>
            </div>
          </div>
        )}

        {/* Live canvas preview — only mount when dialog is open */}
        {open && (
          <JsTexturePreviewCanvas
            key={restartKey}
            file={file}
            onStatusChange={setStatus}
            onError={setError}
            onStatsUpdate={setStats}
            stopped={stopped}
          />
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          {/* Controls */}
          <div className="flex gap-2">
            {status === 'running' ? (
              <Button variant="outline" size="sm" onClick={handleStop}>
                <Square className="mr-1.5 h-3.5 w-3.5" />
                Stop
              </Button>
            ) : status === 'stopped' || status === 'error' ? (
              <Button variant="outline" size="sm" onClick={handleRestart}>
                <Play className="mr-1.5 h-3.5 w-3.5" />
                Restart
              </Button>
            ) : null}
            {status === 'running' && (
              <Button variant="outline" size="sm" onClick={handleRestart}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Restart
              </Button>
            )}
          </div>

          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
